/**
 * Walks up from a target node to the root, recording the child index
 * at each level. This creates an "address" like [0, 1, 0] that
 * uniquely identifies a node's position in the tree.
 */
function getNodePath(root: Node, target: Node): number[] {
    const path: number[] = [];
    let current = target;
    while (current !== root) {
        const parent = current.parentNode;
        if (!parent) {
            return path;
        }
        for (let i = 0; i < parent.childNodes.length; i++) {
            if (parent.childNodes[i] === current) {
                path.unshift(i);
                break;
            }
        }
        current = parent;
    }
    return path;
}

/**
 * Walks down from a root node following the given path of child indices.
 * Used to find the equivalent node in a cloned DOM tree.
 */
function followPath(root: Node, path: number[]): Node {
    let current = root;
    for (const index of path) {
        current = current.childNodes[index];
    }
    return current;
}

/**
 * Creates marked HTML with [[SELECTION]] tags from a DOM selection
 * within a container, preserving HTML structure.
 */
export function getMarkedHtmlFromSelection(container: HTMLElement, range: Range): string {
    // Clone the container to preserve HTML structure
    const clonedContainer = container.cloneNode(true) as HTMLElement;

    // Find the selected nodes in the cloned tree using the range
    const clonedStart = followPath(clonedContainer, getNodePath(container, range.startContainer));
    const clonedEnd = followPath(clonedContainer, getNodePath(container, range.endContainer));

    // Create a range in the cloned tree
    const clonedRange = document.createRange();
    clonedRange.setStart(clonedStart, range.startOffset);
    clonedRange.setEnd(clonedEnd, range.endOffset);

    // Expand start outward if needed (move marker outside opening tags)
    if (range.startOffset === 0 && clonedStart.nodeType === Node.TEXT_NODE) {
        let expandedStart: Node = clonedStart;
        let parent = expandedStart.parentNode;
        while (
            parent &&
            parent !== clonedContainer &&
            parent.firstChild === expandedStart &&
            clonedRange.isPointInRange(parent, parent.childNodes.length)
        ) {
            expandedStart = parent;
            parent = expandedStart.parentNode;
        }
        clonedRange.setStartBefore(expandedStart);
    }

    // Expand end outward if needed (move marker outside closing tags)
    if (
        clonedEnd.nodeType === Node.TEXT_NODE &&
        range.endOffset === (clonedEnd.textContent?.length || 0)
    ) {
        let expandedEnd: Node = clonedEnd;
        let parent = expandedEnd.parentNode;
        while (
            parent &&
            parent !== clonedContainer &&
            parent.lastChild === expandedEnd &&
            clonedRange.isPointInRange(parent, 0)
        ) {
            expandedEnd = parent;
            parent = expandedEnd.parentNode;
        }
        clonedRange.setEndAfter(expandedEnd);
    }

    // Insert markers
    const endMarker = document.createTextNode('[[/SELECTION]]');
    const startMarker = document.createTextNode('[[SELECTION]]');

    const startContainer = clonedRange.startContainer;
    const startOffset = clonedRange.startOffset;

    // Insert end marker first
    clonedRange.collapse(false);
    clonedRange.insertNode(endMarker);

    // Insert start marker
    clonedRange.setStart(startContainer, startOffset);
    clonedRange.collapse(true);
    clonedRange.insertNode(startMarker);

    // Remove ROne tags (copy button and section class)
    clonedContainer.querySelectorAll('.r-one-copy-btn').forEach((el) => el.remove());
    clonedContainer.querySelectorAll('.r-one-output-section').forEach((el) => {
        el.removeAttribute('class');
    });

    return clonedContainer.innerHTML;
}
